import {
  Checkbox,
  CheckboxProps,
  Spinner,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  Button,
  useDisclosure,
  Chip,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@heroui/react";
import dayjs from "dayjs";
import { cn, getTranslatedTimeframe } from "@/utils";
import { plural, t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  GetCustomerSubscriptionsForCustomer,
  PostValidateSubscriptionUse,
} from "@/server-types";
import { AlertTriangle, InfoIcon, Hourglass } from "lucide-react";
import { useAppointmentModalStore } from "../../useAppointmentModalStore_";
import { api } from "@/lib/api-client";
import { useEffect, useState } from "react";
import { CustomerSubscriptionCard } from "@/pages/Dashboard/Customers/CustomerPage/components/Subscriptions/CustomerSubscriptionCard";
import { getPeriodPaymentState } from "@/utils/subscription-shares";
import { getOutstandingSubscriptionPeriods } from "./subscriptionPaymentStatus";
import {
  rankSubscriptionWarnings,
  SubscriptionWarning,
  SubscriptionWarningTone,
} from "./subscriptionWarnings";

export type CustomerSubscription =
  GetCustomerSubscriptionsForCustomer["response"]["customerSubscriptions"][number];

export type ValidationError =
  PostValidateSubscriptionUse["response"]["errors"][number];

export const getValidationErrorMessage = (error: ValidationError): string => {
  switch (error.type) {
    case "SUBSCRIPTION_INACTIVE":
      return t`Paket ni aktiven`;
    case "DATE_INVALID":
      return t`Termin je izven obdobja veljavnosti paketa`;
    case "SERVICE_NOT_INCLUDED":
      return t`Storitev ni vključena v ta paket`;
    case "SUBSCRIPTION_FROZEN":
      return t`Paket bo zamrznjen v času termina`;
    case "LOCATION_NOT_INCLUDED":
      return t`Paketa ni mogoče uporabiti na tej lokaciji`;
    case "LIMITATION_EXCEEDED":
      if (error.limitation) {
        const timeframe = getTranslatedTimeframe(error.limitation.timeframe);
        return t`Presežena bo omejitev: ${error.limitation.limitAmount} obiskov na ${timeframe}`;
      }
      return t`Presežena omejitev uporabe paketa`;
    default:
      return t`Napaka pri preverjanju paketa`;
  }
};

const WARNING_TONE_CLASS: Record<SubscriptionWarningTone, string> = {
  danger: "text-red-500",
  warning: "text-amber-600",
  info: "text-blue-600",
};

function getSubscriptionWarningMessage(warning: SubscriptionWarning): string {
  switch (warning.kind) {
    case "validation":
      return getValidationErrorMessage(warning.error);
    case "outstanding-period":
      return warning.isPartiallyPaid
        ? t`Delno plačano obdobje ${warning.periodStartFormatted} - ${warning.periodEndFormatted}`
        : t`Neplačano obdobje ${warning.periodStartFormatted} - ${warning.periodEndFormatted}`;
    case "not-valid-at-appointment-time":
      return t`Paket ni veljaven v času termina`;
    case "near-limit":
      return t`Omejitev obiskov skoraj dosežena`;
    case "expiring-soon":
      return t`Paket poteče kmalu`;
  }
}

function SubscriptionWarningLine({
  warning,
}: {
  warning: SubscriptionWarning;
}) {
  const Icon = warning.kind === "expiring-soon" ? Hourglass : AlertTriangle;
  return (
    <div
      className={cn(
        "flex items-center gap-2 text-sm font-medium",
        WARNING_TONE_CLASS[warning.tone],
      )}
      data-identifier="subscription-warning"
    >
      <Icon size={16} className="shrink-0" />
      {getSubscriptionWarningMessage(warning)}
    </div>
  );
}

export const CustomerSubscriptionItem = ({
  customerSubscription,
  isSelected,
  isNewAppointment,
  locationId,
  ...checkboxProps
}: Omit<CheckboxProps, "children"> & {
  customerSubscription: CustomerSubscription;
  isSelected: boolean;
  isNewAppointment: boolean;
  locationId: number | null;
}) => {
  const { selectedServices, appointmentData } = useAppointmentModalStore(
    (state) => ({
      selectedServices: state.selectedServices,
      appointmentData: state.appointmentData,
    }),
  );

  const [validationResult, setValidationResult] = useState<{
    isValid: boolean;
    errors: ValidationError[];
  } | null>(null);

  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  const {
    mutateAsync: validateSubscriptionUse,
    isPending: isPendingValidation,
  } = api.appointments.useValidateSubscriptionUse();

  // Outstanding until every payer has settled, so a period the company still owes shows as partly paid.
  const firstUnsettledInvoice = getOutstandingSubscriptionPeriods(
    customerSubscription.invoices,
    (invoice) => getPeriodPaymentState(invoice) === "paid",
  ).sort(
    (a, b) =>
      new Date(a.periodStart).getTime() - new Date(b.periodStart).getTime(),
  )[0];

  const outstandingPeriod = firstUnsettledInvoice
    ? {
        invoice: firstUnsettledInvoice,
        isPartiallyPaid:
          getPeriodPaymentState(firstUnsettledInvoice) === "partially_paid",
      }
    : null;

  const NEAR_LIMIT_THRESHOLD = 2;
  const hasLimitationNearLimit = customerSubscription.limitations.some(
    (limitation) =>
      limitation.timeframe === "all" &&
      limitation.usedAmount >= limitation.limitAmount - NEAR_LIMIT_THRESHOLD,
  );

  const EXPIRING_SOON_DAYS = 7;
  const isExpiringSoon =
    dayjs(customerSubscription.validTo).isAfter(dayjs()) &&
    dayjs(customerSubscription.validTo).isBefore(
      dayjs().add(EXPIRING_SOON_DAYS, "day"),
    );

  const isNotValidAtAppointmentTime = (() => {
    if (!appointmentData?.startTime) return false;

    const appointmentDate = dayjs(appointmentData.startTime);
    const validFrom = dayjs(customerSubscription.validFrom);
    const isBeforeValidFrom = appointmentDate.isBefore(validFrom, "day");

    if (customerSubscription.isRecurring) {
      return isBeforeValidFrom;
    } else {
      const validTo = dayjs(customerSubscription.validTo);
      const isAfterValidTo = appointmentDate.isAfter(validTo, "day");
      return isBeforeValidFrom || isAfterValidTo;
    }
  })();

  useEffect(() => {
    if (!selectedServices.length || !appointmentData?.startTime) {
      setValidationResult(null);
      return;
    }

    const validateSubscription = async () => {
      try {
        const appointments = selectedServices.map(
          (service: { serviceId: number }) => ({
            serviceId: service.serviceId,
            startTimeUtc: new Date(appointmentData.startTime),
            locationId,
          }),
        );

        const result = await validateSubscriptionUse({
          customerSubscriptionId: customerSubscription.id,
          appointments,
        });

        setValidationResult(result);
      } catch (error) {
        console.error("Failed to validate subscription:", error);
        setValidationResult({ isValid: false, errors: [] });
      }
    };

    validateSubscription();
  }, [
    selectedServices,
    appointmentData?.startTime,
    customerSubscription.id,
    locationId,
    validateSubscriptionUse,
  ]);

  const warnings = rankSubscriptionWarnings({
    validationErrors: validationResult?.errors ?? [],
    isNewAppointment,
    outstandingPeriod: outstandingPeriod && {
      isPartiallyPaid: outstandingPeriod.isPartiallyPaid,
      periodStartFormatted: outstandingPeriod.invoice.periodStartFormatted,
      periodEndFormatted: outstandingPeriod.invoice.periodEndFormatted,
    },
    isNotValidAtAppointmentTime,
    hasLimitationNearLimit,
    isExpiringSoon,
  });
  const [topWarning, ...otherWarnings] = warnings;

  if (isPendingValidation) {
    return (
      <Checkbox
        classNames={{
          base: cn(
            "inline-flex max-w-md w-full bg-content1 m-0",
            "hover:bg-content2 items-center justify-start",
            "cursor-pointer rounded-lg gap-2 p-4 border-2 border-transparent",
          ),
          label: "w-full",
        }}
        isDisabled={true}
      >
        <div className="flex w-full flex-col gap-1">
          <Spinner />
        </div>
      </Checkbox>
    );
  }

  return (
    <>
      <div
        className={cn(
          "bg-content1 m-0 inline-flex w-full max-w-md",
          "hover:bg-content2 items-center justify-start",
          "cursor-pointer gap-2 rounded-lg border-2 p-4",
          isSelected ? "border-primary" : "border-transparent",
        )}
      >
        <Checkbox {...checkboxProps} />
        <div
          className="flex w-full flex-col gap-1"
          data-identifier="subscription-item"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-medium">
                {customerSubscription.variation?.subscription?.name ??
                  customerSubscription.name ??
                  t`Paket po meri`}
              </span>
              <Button
                isIconOnly
                startContent={<InfoIcon height={16} width={16} />}
                size="sm"
                variant="flat"
                onPress={onOpen}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm">
              {customerSubscription.variation?.name ?? t`Variacija po meri`}
            </span>
            <span className="text-xs text-gray-500">
              {t`Veljavnost`} {customerSubscription.validFromFormatted}
              {" - "}
              {customerSubscription.validToFormatted}
            </span>
          </div>

          {customerSubscription.credits?.length > 0 && (
            <div className="flex items-center gap-2 pt-1">
              <Chip size="sm" variant="flat" color="primary" radius="sm">
                <Trans>
                  +
                  {customerSubscription.credits.reduce(
                    (sum, credit) => sum + (credit.amount - credit.usedAmount),
                    0,
                  )}{" "}
                  nadomestnih obiskov
                </Trans>
              </Chip>
            </div>
          )}
          {topWarning && (
            <div className="flex items-center gap-2">
              <SubscriptionWarningLine warning={topWarning} />
              {otherWarnings.length > 0 && (
                <Popover placement="bottom-start">
                  <PopoverTrigger>
                    <Button
                      size="sm"
                      variant="flat"
                      radius="full"
                      className="h-6 min-w-0 shrink-0 px-2 font-medium text-xs"
                      aria-label={t`Prikaži vsa opozorila`}
                      data-identifier="subscription-warnings-more"
                    >
                      +{otherWarnings.length}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="max-w-xs items-start gap-2 p-3">
                    <p className="font-medium text-default-500 text-xs">
                      {plural(warnings.length, {
                        one: "# opozorilo",
                        two: "# opozorili",
                        few: "# opozorila",
                        other: "# opozoril",
                      })}
                    </p>
                    {warnings.map((warning, index) => (
                      <SubscriptionWarningLine key={index} warning={warning} />
                    ))}
                  </PopoverContent>
                </Popover>
              )}
            </div>
          )}
          {customerSubscription.autoRenewOnVisitExhaustion && (
            <div className="pt-1">
              <Chip size="sm" variant="bordered" radius="sm">
                <Trans>Samodejna obnova ob porabi</Trans>
              </Chip>
            </div>
          )}
        </div>
      </div>

      <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="2xl">
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader></ModalHeader>
              <ModalBody>
                <CustomerSubscriptionCard
                  customerSubscriptionId={customerSubscription.id}
                />
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
